import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  TableBody,
  Stack,
  Button,
  styled,
  MenuItem,
  ButtonProps,
  SelectChangeEvent
} from "@mui/material";
import { Select, TextField as CommonTextField } from "./forms";
import { CustomMonthPicker, CustomDatePicker } from ".";
import { TAdditionalTableHeader } from "src/types/company";
import { formatToFull, formatUntilMonth } from "src/utils/dateFormatter";
import { getServerLabel } from "src/assets/formatter";
import NiceModal from "@ebay/nice-modal-react";

export type TRow = { [key in string]: string | number };
const TableRow = styled(MuiTableRow)`
  :hover {
    .MuiTableCell-root {
      background-color: #eaeaea;
    }
  }
`;

const TextField = styled(CommonTextField)`
  .MuiInputBase-root {
    background-color: #fff;
  }
`;

const TableCell = styled(MuiTableCell)`
  background-color: #f6f6f6;
  cursor: pointer;
`;

const CellButton = styled((props: ButtonProps) => (
  <Button {...props} variant="outlined" />
))`
  &:not(:last-child) {
    margin-right: 10px;
  }
`;

function RowItem<T>({
  headers,
  row,
  editMode,
  handleUpdate,
  handleAdd,
  onAddCallback,
  handleRemove,
  handleCloseNewRow,
  onChangeValue
}: {
  editMode: boolean;
  headers: TAdditionalTableHeader<keyof T>[];
  row: T;
  handleUpdate?: (item: T) => void;
  handleAdd?: (item: T, callback?: () => void) => void;
  onAddCallback?: () => void;
  handleRemove?: (item: T) => void;
  handleCloseNewRow?: () => void;
  onChangeValue?: (item: T) => T;
}) {
  const [item, setItem] = useState<T>(row);

  useEffect(() => {
    setItem(row);
  }, [row]);

  const handleValue = useCallback(
    (k: string, v: string | number) => {
      setItem(curr => {
        const result = {
          ...curr,
          [k]: v
        };

        return onChangeValue ? onChangeValue(result) : result;
      });
    },
    [onChangeValue]
  );

  return (
    <TableRow>
      {headers.map((headerItem, item_idx) => {
        const cellValue = item[headerItem.key];

        return (
          <TableCell key={item_idx}>
            {editMode ? (
              <ItemByType<T>
                headerItem={headerItem}
                row={item}
                onChangeValue={handleValue}
              />
            ) : headerItem.selectItems && typeof cellValue === "string" ? (
              getServerLabel(headerItem.selectItems, cellValue)
            ) : (
              `${cellValue}`
            )}
          </TableCell>
        );
      })}
      {editMode && (
        <TableCell align="center">
          {handleUpdate && (
            <CellButton onClick={() => handleUpdate(item)}>수정</CellButton>
          )}
          {handleAdd && (
            <CellButton
              onClick={() => handleAdd(item, onAddCallback)}
              disabled={Object.values(item).filter(v => !!v).length < 1}
            >
              등록
            </CellButton>
          )}
          {handleRemove && (
            <CellButton
              onClick={() => {
                NiceModal.show("confirm", {
                  title: "정말로 삭제하시겠습니까?",
                  message: "이 동작은 되돌릴 수 없습니다."
                }).then(() => handleRemove(item));
              }}
            >
              삭제
            </CellButton>
          )}
          {handleCloseNewRow && (
            <CellButton onClick={handleCloseNewRow}>취소</CellButton>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

function ItemByType<T>({
  headerItem,
  row,
  onChangeValue
}: {
  headerItem: TAdditionalTableHeader<keyof T>;
  row: T;
  onChangeValue: (k: string, v: string | number) => void;
}) {
  const onTextChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const {
        currentTarget: { value, id }
      } = event;

      onChangeValue(id, value);
    },
    [onChangeValue]
  );

  const onNumberChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const {
        currentTarget: { value, id }
      } = event;

      onChangeValue(id, Number(value));
    },
    [onChangeValue]
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const {
        target: { value }
      } = event;

      onChangeValue(headerItem.key as string, value);
    },
    [onChangeValue, headerItem.key]
  );

  const cellValue = useMemo(() => row[headerItem.key], [row, headerItem.key]);

  if (headerItem.type === "string") {
    return (
      <TextField
        fullWidth
        id={headerItem.key}
        value={cellValue}
        size="small"
        placeholder={headerItem.placeholder}
        onChange={onTextChange}
        disabled={headerItem.disable}
      />
    );
  }

  if (headerItem.type === "number") {
    return (
      <TextField
        type="number"
        fullWidth
        id={headerItem.key}
        value={cellValue}
        size="small"
        placeholder={headerItem.placeholder}
        onChange={onNumberChange}
        disabled={headerItem.disable}
      />
    );
  }

  if (headerItem.type === "select") {
    if (!headerItem.selectItems) {
      throw "None selectItems.";
    }

    return (
      <Select
        id={headerItem.key}
        size="small"
        value={cellValue}
        onChange={handleSelectChange}
        fullWidth
        displayEmpty
        disabled={headerItem.disable}
      >
        <MenuItem value="">선택</MenuItem>
        {headerItem.selectItems.map(item => (
          <MenuItem value={item.code_mgmt_id} key={item.code_mgmt_id}>
            {item.code_mgmt_nm}
          </MenuItem>
        ))}
      </Select>
    );
  }

  let result: undefined | Date = undefined;
  if (typeof cellValue === "string" && !!cellValue) {
    result = new Date(cellValue);
  }
  if (headerItem.type === "date") {
    return (
      <CustomDatePicker
        startDate={result}
        onChange={v => onChangeValue(headerItem.key as string, formatToFull(v))}
      />
    );
  }

  if (headerItem.type === "date_year_month") {
    return (
      <CustomMonthPicker
        selected={result}
        onChange={v =>
          onChangeValue(headerItem.key as string, formatUntilMonth(v))
        }
      />
    );
  }

  throw "Not support type.";
}

function AdditionalTable<TObj>({
  title = " ",
  headers,
  rows,
  editMode = false,
  emptyMessage,
  handleUpdate,
  handleAdd,
  handleRemove,
  handleValue,
  initRow,
  disabledAddMessage
}: {
  title?: string;
  headers: TAdditionalTableHeader<keyof TObj>[];
  rows: TObj[];
  editMode?: boolean;
  emptyMessage?: string;
  handleUpdate: (item: TObj) => void;
  handleAdd: (item: TObj, callback?: () => void) => void;
  handleRemove: (item: TObj) => void;
  handleValue?: (item: TObj) => TObj;
  initRow: TObj;
  disabledAddMessage?: string;
}) {
  const [newRow, setNewRow] = useState(false);

  useEffect(() => {
    if (!editMode) {
      setNewRow(false);
    }
  }, [editMode]);

  return (
    <Box>
      <Stack
        justifyContent="space-between"
        gap="8px"
        direction="row"
        alignItems="center"
        mb="4px"
      >
        {title && <Typography variant="h6">{title}</Typography>}
        {editMode && (
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (disabledAddMessage) {
                NiceModal.show("alert", {
                  title: disabledAddMessage
                });
              } else {
                setNewRow(true);
              }
            }}
            disabled={newRow}
          >
            추가
          </Button>
        )}
      </Stack>

      <Divider />
      <TableContainer>
        <Table
          aria-label="custom pagination table"
          sx={{
            whiteSpace: "nowrap"
          }}
        >
          <TableHead>
            <MuiTableRow>
              {headers.map((header, idx) => (
                <MuiTableCell key={idx}>
                  <Typography variant="h6">{header.label}</Typography>
                </MuiTableCell>
              ))}
              {editMode && <MuiTableCell />}
            </MuiTableRow>
          </TableHead>

          <TableBody>
            {rows.length < 1 && !newRow ? (
              <TableRow>
                <TableCell colSpan={headers.length + 1} align="center">
                  {emptyMessage || "데이터가 없습니다."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, row_idx) => (
                <RowItem<TObj>
                  key={row_idx}
                  editMode={editMode}
                  headers={headers}
                  row={row}
                  handleUpdate={handleUpdate}
                  handleRemove={handleRemove}
                  onChangeValue={handleValue}
                />
              ))
            )}
            {newRow && (
              <RowItem<TObj>
                editMode={editMode}
                headers={headers}
                row={initRow}
                handleAdd={handleAdd}
                onAddCallback={() => setNewRow(false)}
                handleCloseNewRow={() => setNewRow(false)}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
    </Box>
  );
}

export default AdditionalTable;

export function AdditionalTableForAdd<TObj>({
  title,
  headers,
  rows,
  onAddRow,
  handleValue,
  handleRemove
}: {
  title: string;
  headers: TAdditionalTableHeader<keyof TObj>[];
  rows: TObj[];
  onAddRow: () => void;
  handleValue: (item: React.FormEvent<HTMLInputElement>) => void;
  handleRemove: (idx: number) => void;
}) {
  return (
    <Box>
      <Stack
        justifyContent="space-between"
        gap="8px"
        direction="row"
        alignItems="center"
        mb="4px"
      >
        <Typography variant="h6">{title}</Typography>
        <Button size="small" variant="contained" onClick={onAddRow}>
          추가
        </Button>
      </Stack>

      <Divider />
      <TableContainer>
        <Table
          aria-label="custom pagination table"
          sx={{
            whiteSpace: "nowrap"
          }}
        >
          <TableHead>
            <MuiTableRow>
              {headers.map((header, idx) => (
                <MuiTableCell key={idx}>
                  <Typography variant="h6">{header.label}</Typography>
                </MuiTableCell>
              ))}
              {/* 버튼자리 */}
              <MuiTableCell />
            </MuiTableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, row_idx) => (
              <TableRow key={row_idx}>
                {headers.map((headerItem, item_idx) => {
                  const cellValue = row[headerItem.key];

                  return (
                    <TableCell key={item_idx}>
                      <TextField
                        fullWidth
                        id={headerItem.key}
                        name={row_idx.toString()}
                        value={cellValue}
                        size="small"
                        placeholder={headerItem.placeholder}
                        onChange={handleValue}
                        disabled={headerItem.disable}
                      />
                    </TableCell>
                  );
                })}

                <TableCell align="center">
                  <CellButton
                    onClick={() => {
                      handleRemove(row_idx);
                    }}
                  >
                    삭제
                  </CellButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
    </Box>
  );
}
